import * as React from 'react';
import { toIntent } from '../intent';
import { dynamic, assignProps } from '../view';
import { useIsomorphicLayoutEffect } from '../util';
import { useCurrentTimeline } from './timeline';
import { NodeAnimation } from './animate';
const { useRef } = React;
function shouldSkipAnimations() {
    return process.env.NODE_ENV === 'test' && // If PLAYWRIGHT_TESTS_ANIMATIONS_ENABLED is defined, only skip animations if it's false
    (typeof window === 'undefined' || !('PLAYWRIGHT_TESTS_ANIMATIONS_ENABLED' in window) || window.PLAYWRIGHT_TESTS_ANIMATIONS_ENABLED === false);
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopIntent = dynamic(()=>{});
export function normalizeKeyframe(keyframe) {
    const { delay, duration, easing, disabled, ...styles } = keyframe;
    return {
        styles: styles,
        options: {
            delay,
            duration,
            easing,
            disabled
        }
    };
}
function refAnimation(animationRef, styles, options) {
    if (shouldSkipAnimations()) {
        return noopIntent;
    }
    return assignProps(()=>{
        const ref = useRef(null);
        const timeline = useCurrentTimeline();
        useIsomorphicLayoutEffect(()=>{
            if (animationRef.current === null) {
                animationRef.current = new NodeAnimation(timeline, ref, styles, options);
            }
        }, [
            timeline
        ]);
        return {
            ref
        };
    });
}
function conditionalAnimation(condition, styles, options) {
    if (shouldSkipAnimations()) {
        return noopIntent;
    }
    return assignProps(()=>{
        const ref = useRef(null);
        const pushedTask = useRef(null);
        const timeline = useCurrentTimeline();
        const animationRef = useRef(null);
        useIsomorphicLayoutEffect(()=>{
            if (animationRef.current === null) {
                animationRef.current = new NodeAnimation(timeline, ref, styles, options);
            }
            const animation = animationRef.current;
            if (condition && !options.disabled) {
                if (pushedTask.current == null) {
                    pushedTask.current = animation.play({
                        push: true
                    });
                }
            } else if (ref.current && pushedTask.current != null) {
                pushedTask.current.removeAndPlay();
                pushedTask.current = null;
            }
        });
        return {
            ref: ref
        };
    });
}
function presenceAnimation(keyframe, type, reverse) {
    if (shouldSkipAnimations()) {
        return noopIntent;
    }
    const { styles, options } = normalizeKeyframe(keyframe);
    if (reverse) {
        options.reverse = reverse;
    }
    return assignProps(()=>{
        const ref = useRef(null);
        const timeline = useCurrentTimeline();
        const { disabled } = options;
        useIsomorphicLayoutEffect(()=>{
            let animation = null;
            if (!disabled) {
                animation = new NodeAnimation(timeline, ref, styles, options);
                return timeline.register(type, animation);
            }
        }, [
            timeline,
            disabled
        ]);
        return {
            ref: ref
        };
    });
}
export function transition(keyframe) {
    const { styles, options } = normalizeKeyframe(keyframe);
    return {
        when: (condition)=>conditionalAnimation(condition, styles, options),
        ref: (ref)=>refAnimation(ref, styles, options)
    };
}
// animate.in
export function transitionIn(keyframe) {
    return presenceAnimation(keyframe, 'in', 'keyframes');
}
// animate.out
export function transitionOut(keyframe) {
    return presenceAnimation(keyframe, 'out');
}
export function transitionInOut(keyframe) {
    return toIntent([
        presenceAnimation(keyframe, 'in', 'keyframes'),
        presenceAnimation(keyframe, 'out', 'effects')
    ]);
}

//# sourceMappingURL=transitions.js.map